import { CalendarDays, Lock, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@/components/ui';
import DatePicker from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select-menu';
import { usePaymentInstallments, useSavePaymentInstallments } from '@/hooks';
import { usePaymentInstallmentDays } from '@/hooks/useCompanySettings';
import { inr, type SetupQuoteResult } from '@/lib/sales-metrics';
import { type PaymentInstallmentRow } from '@/services/leads.service';
import { errorMessage } from '@/utils/apiError';
import { RENEWAL_FREQUENCIES, type RenewalFrequencyValue } from '@/utils/constants';

// ----- date helpers -------------------------------------------------------

const toDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return toDateStr(d);
};

// Spread N due-dates evenly from today+step to today+totalDays.
// For N=1: [today+totalDays]. For N=2: [today+totalDays/2, today+totalDays], etc.
const spreadDates = (n: number, totalDays: number): string[] =>
  Array.from({ length: n }, (_, i) => addDays(Math.round(((i + 1) * totalDays) / n)));

// Split `total` into `n` integer buckets; last bucket absorbs the remainder.
const splitAmounts = (total: number, n: number): number[] => {
  if (n <= 0) return [];
  const base = Math.floor(total / n);
  const last = total - base * (n - 1);
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? last : base));
};

// A row is "paid" once a transaction has been recorded (both a transaction no.
// and a mode are set in the Billing Handover step). A 'Due' row carries a mode
// but no transaction number (ADR-053), so it correctly reads as unpaid here.
const isPaid = (row: PaymentInstallmentRow): boolean =>
  Boolean(row.transactionNo?.trim()) && Boolean(row.mode);

// A row is frozen once it is paid or handed over to billing: its amount can no
// longer change and it cannot be deleted. A handed-over 'Due' row is frozen too
// - billing was told to invoice that amount, even though nothing has been
// received against it. Only the still-editable rows share what is left of the
// total, so the schedule always sums to the payable amount.
const isFrozen = (row: PaymentInstallmentRow): boolean => isPaid(row) || Boolean(row.handedOverAt);

// -------------------------------------------------------------------------

interface StepPaymentPlanProps {
  leadId: number;
  /** The purchase this schedule belongs to (ADR-048), so reads and writes
   * cannot land on a different order of the same client. */
  orderId: number | null;
  setupQuote: SetupQuoteResult | null;
  isDone: boolean;
  onSaveProceed: () => void;
  // Products in this order, and how often each of them renews once billing
  // enables service. Cadence is per PRODUCT (ADR-048) so one order can mix an
  // annual plan with a quarterly add-on. It lives with the payment plan (not the
  // handover) because it is a payment-cadence decision the RM makes while
  // scheduling collection.
  selectedProducts: string[];
  productNames: Record<string, string>;
  productRenewalFrequencies: Record<string, RenewalFrequencyValue>;
  onChangeProductFrequency: (code: string, value: RenewalFrequencyValue) => void;
}

const MAX_INSTALLMENTS = 3;

// Additional ARNs are not independently renewable - their fee folds into the
// parent AUM-based product's renewal (ADR-048) - so they get no cadence control.
const ADD_ARN_CODE = 'ADD_ARN';

export function StepPaymentPlan({
  leadId,
  orderId,
  setupQuote,
  isDone,
  onSaveProceed,
  selectedProducts,
  productNames,
  productRenewalFrequencies,
  onChangeProductFrequency,
}: StepPaymentPlanProps) {
  const { toast } = useToast();
  const { data: adminDays = 30 } = usePaymentInstallmentDays();
  const { data: savedInstallments, isLoading } = usePaymentInstallments(leadId, orderId);
  const saveInstallments = useSavePaymentInstallments();
  const skipInitRef = useRef(true);
  const [rows, setRows] = useState<PaymentInstallmentRow[]>([]);
  const [initialized, setInitialized] = useState(false);

  const renewableProducts = selectedProducts.filter((code) => code !== ADD_ARN_CODE);

  const updateFrequency = (code: string, value: string) => {
    const next = RENEWAL_FREQUENCIES.find((f) => f === value);
    if (!next || next === productRenewalFrequencies[code]) return;
    onChangeProductFrequency(code, next);
  };

  const grandTotal = Math.round(setupQuote?.grandTotal ?? 0);

  // Amount already committed (paid rows) and the remaining due that the still-
  // unpaid rows must share.
  const frozenSum = rows.filter(isFrozen).reduce((s, r) => s + r.amount, 0);
  const due = Math.max(0, grandTotal - frozenSum);
  const editableCount = rows.filter((r) => !isFrozen(r)).length;

  // Re-split what is left across the editable rows, keeping frozen ones exactly
  // as they are, and renumber the whole schedule 1..N in place.
  const redistribute = useCallback(
    (input: PaymentInstallmentRow[]): PaymentInstallmentRow[] => {
      const frozenTotal = input.filter(isFrozen).reduce((s, r) => s + r.amount, 0);
      const remaining = Math.max(0, grandTotal - frozenTotal);
      const editableIdxs = input
        .map((r, i) => ({ r, i }))
        .filter((x) => !isFrozen(x.r))
        .map((x) => x.i);
      const amounts = splitAmounts(remaining, editableIdxs.length);
      const amountByIdx = new Map(editableIdxs.map((idx, k) => [idx, amounts[k]]));
      return input.map((r, i) => ({
        ...r,
        installmentNumber: i + 1,
        amount: isFrozen(r) ? r.amount : (amountByIdx.get(i) ?? 0),
      }));
    },
    [grandTotal],
  );

  const buildDefault = useCallback(
    (n: number): PaymentInstallmentRow[] => {
      const amounts = splitAmounts(grandTotal, n);
      const dates = spreadDates(n, adminDays);
      return amounts.map((amount, i) => ({
        installmentNumber: i + 1,
        amount,
        dueDate: dates[i],
        transactionNo: null,
        mode: null,
        handedOverAt: null,
      }));
    },
    [adminDays, grandTotal],
  );

  // Initialize rows once: prefer DB data, fall back to default 1-installment.
  useEffect(() => {
    if (isLoading || initialized) return;
    if (savedInstallments !== undefined) {
      setRows(savedInstallments.length > 0 ? savedInstallments : buildDefault(1));
      setInitialized(true);
    }
  }, [isLoading, savedInstallments, initialized, buildDefault]);

  const installmentSum = rows.reduce((s, r) => s + r.amount, 0);
  const sumMismatch = rows.length > 1 && grandTotal > 0 && installmentSum !== grandTotal;

  // Debounced save whenever rows change (skips the very first initialization).
  // Skipped while the amounts don't reconcile to the total - the server rejects
  // a mismatched schedule, so autosaving it would only produce spurious errors;
  // the RM still sees the amber warning and can Save & Continue when balanced.
  useEffect(() => {
    if (skipInitRef.current) {
      skipInitRef.current = false;
      return undefined;
    }
    if (!initialized || rows.length === 0 || sumMismatch) return undefined;
    const handle = setTimeout(() => {
      saveInstallments.mutate(
        { id: leadId, installments: rows, orderId },
        {
          // Surface the server's rejection (e.g. a paid installment can't change)
          // instead of failing silently.
          onError: (err) => toast.error(errorMessage(err, 'Could not save payment plan')),
        },
      );
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateAmount = (idx: number, raw: string) => {
    setRows((prev) => {
      if (isFrozen(prev[idx])) return prev;
      const parsed = parseInt(raw, 10);
      const amount = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
      const updated = prev.map((r, i) => (i === idx ? { ...r, amount } : r));
      // Auto-adjust the last UNPAID row so the schedule still totals grandTotal,
      // but only when the user edits a different unpaid row.
      const editable = updated
        .map((r, i) => ({ r, i }))
        .filter((x) => !isFrozen(x.r))
        .map((x) => x.i);
      const lastEditableIdx = editable[editable.length - 1];
      if (lastEditableIdx !== undefined && lastEditableIdx !== idx && grandTotal > 0) {
        const sumOthers = updated.reduce(
          (s, r, i) => (i === lastEditableIdx ? s : s + r.amount),
          0,
        );
        return updated.map((r, i) =>
          i === lastEditableIdx ? { ...r, amount: Math.max(0, grandTotal - sumOthers) } : r,
        );
      }
      return updated;
    });
  };

  const updateDate = (idx: number, value: string) => {
    // Due dates are mandatory (the server rejects an empty dueDate). Ignore a
    // cleared value so the DatePicker's Clear button can't leave the plan in an
    // unsaveable state - the row keeps its previous date.
    if (!value) return;
    setRows((prev) =>
      isFrozen(prev[idx]) ? prev : prev.map((r, i) => (i === idx ? { ...r, dueDate: value } : r)),
    );
  };

  const addInstallment = () => {
    if (rows.length >= MAX_INSTALLMENTS || due <= 0) return;
    skipInitRef.current = false;
    const newRow: PaymentInstallmentRow = {
      installmentNumber: rows.length + 1,
      amount: 0,
      dueDate: spreadDates(1, adminDays)[0],
      transactionNo: null,
      mode: null,
      handedOverAt: null,
    };
    setRows(redistribute([...rows, newRow]));
  };

  // Only the trailing unpaid row can be removed, so paid rows keep their
  // installment numbers (the server rejects changing/renumbering a paid row).
  const removeLast = () => {
    if (rows.length <= 1) return;
    const last = rows[rows.length - 1];
    if (isFrozen(last)) return;
    skipInitRef.current = false;
    setRows(redistribute(rows.slice(0, -1)));
  };

  const handleSaveProceed = () => {
    if (rows.length === 0) {
      toast.error('Add at least one installment');
      return;
    }
    if (sumMismatch) {
      toast.error(`Installment amounts must total ${inr(grandTotal)}`);
      return;
    }
    saveInstallments.mutate(
      { id: leadId, installments: rows, orderId },
      {
        onSuccess: () => onSaveProceed(),
        // Surface the server's reason (e.g. "cannot change the plan once a
        // transaction has been made") instead of a generic line.
        onError: (err) => toast.error(errorMessage(err, 'Could not save payment plan')),
      },
    );
  };

  const lastIdx = rows.length - 1;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-ink">Payment Plan</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Schedule how the client will pay. Up to {MAX_INSTALLMENTS} installments - amounts and
          dates are editable until an installment is handed over to billing. Adding an installment
          splits the amount still due across the remaining rows.
        </p>
      </div>

      {/* Summary bar */}
      {setupQuote && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
          <div className="flex items-center gap-1.5 text-sm">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Total payable:</span>
            <span className="font-semibold text-ink">{inr(grandTotal)}</span>
          </div>
          {frozenSum > 0 && (
            <span className="text-sm text-muted-foreground">
              Due (unpaid): <span className="font-semibold text-ink">{inr(due)}</span>
            </span>
          )}
          <span className="text-sm text-muted-foreground">
            Default span: <span className="font-medium text-ink">{adminDays} days</span>
          </span>
          {rows.length > 1 && (
            <span
              className={`text-sm ${sumMismatch ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}
            >
              Sum: <span className="font-semibold">{inr(installmentSum)}</span>
            </span>
          )}
        </div>
      )}

      {/* Installments table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs text-muted-foreground">
              <th className="pb-2 pr-4 text-left font-medium w-8">#</th>
              <th className="pb-2 pr-4 text-left font-medium">Amount (₹)</th>
              <th className="pb-2 pr-4 text-left font-medium">Due Date</th>
              <th className="pb-2 w-10">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, idx) => {
              const locked = isFrozen(row);
              const lockReason = row.handedOverAt
                ? 'Handed over to billing - locked'
                : 'Transaction recorded - plan locked for this installment';
              return (
                <tr key={row.installmentNumber}>
                  <td className="py-2.5 pr-4">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                      {idx + 1}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={row.amount === 0 ? '' : row.amount}
                      placeholder="0"
                      onChange={(e) => updateAmount(idx, e.target.value)}
                      disabled={locked}
                      className="w-36"
                      aria-label={`Installment ${idx + 1} amount`}
                    />
                  </td>
                  <td className="py-2.5 pr-4">
                    <DatePicker
                      value={row.dueDate}
                      onChange={(value) => updateDate(idx, value)}
                      disabled={locked}
                      className="w-44"
                    />
                  </td>
                  <td className="py-2.5">
                    {locked ? (
                      <span
                        className="flex h-8 w-8 items-center justify-center text-muted-foreground"
                        title={lockReason}
                      >
                        <Lock className="h-3.5 w-3.5" />
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
                        disabled={rows.length <= 1 || idx !== lastIdx}
                        onClick={removeLast}
                        aria-label={`Remove installment ${idx + 1}`}
                        title={
                          idx !== lastIdx
                            ? 'Only the last installment can be removed'
                            : 'Remove installment'
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={rows.length >= MAX_INSTALLMENTS || due <= 0}
          onClick={addInstallment}
          className="gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Installment
        </Button>
        {rows.length >= MAX_INSTALLMENTS && (
          <span className="text-xs text-muted-foreground">
            Maximum {MAX_INSTALLMENTS} installments
          </span>
        )}
        {rows.length < MAX_INSTALLMENTS && due <= 0 && editableCount > 0 && (
          <span className="text-xs text-muted-foreground">Nothing left to schedule</span>
        )}
      </div>

      {sumMismatch && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Installment amounts sum to {inr(installmentSum)}, not the total payable {inr(grandTotal)}.
          Edit any non-last row to auto-correct.
        </p>
      )}

      {/* Renewal cadence, chosen PER PRODUCT (ADR-048) - how often each
          subscription is re-billed once billing enables service. The renewals
          queue snapshots it when that product's first cycle is created; the
          biller can still override it per renewal. Saved through the same
          debounced product-details save as the rest of Step 2, independent of
          the installment schedule above. */}
      {renewableProducts.length > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-3">
          <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <RefreshCw className="h-4 w-4" />
            Renewal Frequency
          </h4>
          <div className="flex flex-col gap-2">
            {renewableProducts.map((code) => (
              <div key={code} className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm text-ink">{productNames[code] ?? code}</span>
                <Select
                  value={productRenewalFrequencies[code] ?? 'Annual'}
                  onChange={(e) => updateFrequency(code, e.target.value)}
                  className="h-8 w-40"
                  aria-label={`Renewal frequency for ${productNames[code] ?? code}`}
                >
                  {RENEWAL_FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            How often each subscription renews after onboarding.
            {selectedProducts.includes(ADD_ARN_CODE) && (
              <> Additional ARNs renew with the MFBOX plan they attach to.</>
            )}
          </p>
        </div>
      )}

      <div className="flex items-center justify-end border-t border-border pt-4">
        <Button
          type="button"
          onClick={handleSaveProceed}
          disabled={saveInstallments.isPending || rows.length === 0}
        >
          {isDone ? 'Save Changes' : 'Save & Continue'}
        </Button>
      </div>
    </div>
  );
}
